// @flow

import React, { Fragment, Component } from 'react';
import { Spinner, Alert, Intent, Checkbox } from '@blueprintjs/core';
import RolePicker from '../pickers/user-role-picker';
import NoAreasPanel from '../no-areas-panel';
import {
  TableHeadings,
  TableHeadingColumn,
  RolesHeadingColumn,
  FirstCellHeadingColumn,
  TableRowItem,
  TableRow,
  Table,
  StyledButton,
  AlertSpinner
} from './table';

import * as UserListModel from '../../models/user-list';
import * as UserModel from '../../models/user';
import * as RoleModel from '../../models/role';
import Pagination from '../pagination';
import PreferencesCell from '../user-profile/preferences-cell';
import type { UserProfilePermissionsT } from '../user-profile/use-user-profile-permissions';

type Props = {
  users: UserListModel.t,
  loading?: boolean,
  currentUser: ?UserModel.t,
  addRole(user: UserListModel.userT, role: RoleModel.catalogueRoleT): mixed,
  removeRole(user: UserListModel.userT, roleId: number): mixed,
  deleteUser(userId: number, userType: UserListModel.userListTypeEnumT): mixed,
  rolesCatalogue: RoleModel.RolesCatalogueT,
  rolesIncludeLoaded: { [UserListModel.userListTypeEnumT]: boolean },
  permission: boolean,
  selectable?: boolean,
  selectedIds?: Set<number>,
  onToggleRow?: (id: number) => mixed,
  onToggleAll?: (visibleIds: number[]) => mixed,
  filterPredicate?: (user: UserListModel.userT) => boolean,
  permissions?: UserProfilePermissionsT,
};

type State = {
  userToDelete: ?UserListModel.userT,
  deleteUserInProgress: boolean,
  roleUpdatingForUser: ?number,
  page: number,
};

const itemsPerPage = 25;

class ActiveUsersTable extends Component<Props, State> {
  state = {
    userToDelete: null,
    deleteUserInProgress: false,
    roleUpdatingForUser: null,
    page: 0,
  };

  headerCheckboxRef: { current: ?HTMLInputElement } = { current: null };

  setUserToDelete(userToDelete: ?UserListModel.userT) {
    this.setState({ userToDelete });
  }

  async handleDeleteConfirm(userId: number) {
    const { deleteUser } = this.props;
    const { userToDelete } = this.state;

    if (userToDelete) {
      this.setState({ deleteUserInProgress: true });
      await deleteUser(userToDelete.id, userToDelete.userListType);
      this.setState({ userToDelete: null, deleteUserInProgress: false });
    }
  }

  async toggleRole(user: UserListModel.userT, role: RoleModel.catalogueRoleT) {
    this.setState({ roleUpdatingForUser: user.id });
    if (user && user.roles && !!user.roles.find(r => r.id === role.id)) {
      await this.props.removeRole(user, role.id);
    } else {
      await this.props.addRole(user, role);
    }
    this.setState({ roleUpdatingForUser: null });
  }

  setPage(page: number) {
    this.setState({
      page,
    });
  }

  componentDidUpdate() {
    const { selectable, selectedIds } = this.props;
    if (!selectable) return;
    const filtered = this.getFilteredUsers();
    const start = this.state.page * itemsPerPage;
    const end = Math.min(start + itemsPerPage, filtered.length);
    const visibleUsers = filtered.slice(start, end);
    const visibleIds = visibleUsers.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 &&
      !!selectedIds &&
      visibleIds.every((id) => selectedIds.has(id));
    const someSelected =
      !!selectedIds &&
      visibleIds.some((id) => selectedIds.has(id)) &&
      !allSelected;
    if (this.headerCheckboxRef.current) {
      this.headerCheckboxRef.current.indeterminate = someSelected;
    }
  }

  getFilteredUsers(): UserListModel.userT[] {
    const { users, filterPredicate } = this.props;
    if (!users) return [];
    if (!filterPredicate) return users;
    return users.filter(filterPredicate);
  }

  render() {
    const {
      loading,
      currentUser,
      permission,
      rolesCatalogue,
      rolesIncludeLoaded,
      selectable,
      selectedIds,
      onToggleRow,
      onToggleAll,
      permissions,
    } = this.props;
    const {
      userToDelete,
      deleteUserInProgress,
      roleUpdatingForUser,
      page,
    } = this.state;

    if (loading) {
      return <NoAreasPanel message={<Spinner size={22} />} />;
    }

    if (!permission) {
      return <NoAreasPanel message="You do not have permission to view the users list" />;
    }

    const filtered = this.getFilteredUsers();

    if (!filtered || filtered.length === 0) {
      return <NoAreasPanel message="No users" />;
    }

    const start = page * Math.max(0, itemsPerPage);
    const end = itemsPerPage > 0 ? Math.min(start + itemsPerPage, filtered.length) : filtered.length;
    if (start > end) this.setPage(0);
    const visibleUsers = filtered.slice(start, end);
    const visibleIds = visibleUsers.map((u) => u.id);

    const allSelected =
      selectable &&
      selectedIds &&
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedIds.has(id));
    const someSelected =
      selectable &&
      selectedIds &&
      visibleIds.some((id) => selectedIds.has(id)) &&
      !allSelected;

    return (
      <Fragment>
        <Table>
          <thead>
            <TableHeadings>
              {selectable && (
                <TableHeadingColumn style={{ width: 40 }}>
                  <Checkbox
                    checked={!!allSelected}
                    onChange={() =>
                      onToggleAll && onToggleAll(visibleIds)
                    }
                    aria-label="Select all visible"
                    aria-checked={
                      someSelected ? 'mixed' : allSelected ? 'true' : 'false'
                    }
                    inputRef={(el) => {
                      this.headerCheckboxRef.current = el;
                    }}
                    style={{ margin: 0 }}
                  />
                </TableHeadingColumn>
              )}
              <FirstCellHeadingColumn>Active Users</FirstCellHeadingColumn>
              <TableHeadingColumn>Name</TableHeadingColumn>
              <RolesHeadingColumn>Roles</RolesHeadingColumn>
              <TableHeadingColumn>Preferences</TableHeadingColumn>
              <TableHeadingColumn>Actions</TableHeadingColumn>
            </TableHeadings>
          </thead>
          <tbody>
            {visibleUsers.map(user => {
              const isCurrentLoggedUser =
                !!currentUser && user.email === currentUser.email;

              return (
                <TableRow key={`active-user-key-${user.id}`}>
                  {selectable && (
                    <TableRowItem>
                      <Checkbox
                        checked={!!selectedIds && selectedIds.has(user.id)}
                        onChange={() => onToggleRow && onToggleRow(user.id)}
                        aria-label="Select user"
                        style={{ margin: 0 }}
                      />
                    </TableRowItem>
                  )}
                  <TableRowItem>{user.email}</TableRowItem>
                  <TableRowItem>{user.fullName}</TableRowItem>
                  <TableRowItem>
                    <RolePicker
                      disabled={isCurrentLoggedUser}
                      rolesCatalogue={rolesCatalogue}
                      rolesDataAvailable={rolesIncludeLoaded[user.userListType]}
                      selectedRoles={user.roles}
                      onToggle={role => this.toggleRole(user, role)}
                      updating={roleUpdatingForUser === user.id}
                    />
                  </TableRowItem>
                  <TableRowItem>
                    {permissions && (
                      <PreferencesCell
                        userId={user.id}
                        permissions={permissions}
                      />
                    )}
                  </TableRowItem>
                  <TableRowItem>
                    <StyledButton
                      text="Remove user"
                      minimal
                      outlined
                      disabled={isCurrentLoggedUser}
                      onClick={() => this.setUserToDelete(user)}
                      intent={isCurrentLoggedUser ? '' : 'danger'}
                    />
                  </TableRowItem>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
        <Pagination pages={filtered.length / itemsPerPage} setPage={this.setPage.bind(this)} currentPage={page} />

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete user"
          icon={deleteUserInProgress ? <AlertSpinner size="40" /> : 'trash'}
          intent={Intent.DANGER}
          isOpen={!!userToDelete}
          onCancel={() => {
            this.setUserToDelete(null);
          }}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to delete {userToDelete && userToDelete.email}
            ?
          </p>
        </Alert>
      </Fragment>
    );
  }
}

export default ActiveUsersTable;
